import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    CheckCircleOutline,
    Email as EmailIcon,
    ErrorOutline,
    Forum as PortalIcon,
    InboxOutlined,
    People as PeopleIcon,
    RemoveCircleOutline,
    Schedule,
    Sms as SmsIcon,
    SwapHoriz,
    TransferWithinAStation,
} from '@material-ui/icons'

import {
    DeliveryLogChannel,
    DeliveryLogStatus,
    EnrollmentStatus,
    RenewalOffer,
    RenewalSequence,
} from '../../../models/Concession'
import {
    useOfferDeliveryLogs,
    useOfferEnrollment,
} from '../../../hooks/useRenewalOffers'
import { displayDateTime } from '../../Concessions/utils'
import { formatCurrency, renewalStatusLabel } from '../../RenewalOffers/utils'
import {
    offerBoardStage,
    signingSubstatus,
    signingSubstatusLabel,
} from './renewalsBoard'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginBottom: theme.spacing(2.5),
    },
    sectionTitle: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1.25, 1.5),
    },
    fieldLabel: {
        fontSize: '0.625rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: 2,
    },
    fieldValue: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        wordBreak: 'break-word',
    },
    alert: {
        border: '1px solid #fecdd3',
        borderRadius: 8,
        backgroundColor: '#fff1f2',
        padding: theme.spacing(1.25, 1.5),
        marginBottom: theme.spacing(1.5),
    },
    card: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    cardTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: theme.palette.grey[50],
    },
    cardBody: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.5),
    },
    changeRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    empty: {
        color: theme.palette.text.disabled,
        fontSize: '0.8125rem',
    },
    logsHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontSize: '0.625rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            padding: theme.spacing(0.75, 1),
        },
    },
    logsBody: {
        '& .MuiTableCell-body': {
            padding: theme.spacing(1),
        },
    },
}))

const ENROLLMENT_STATUS: Record<
    EnrollmentStatus,
    { bg: string; fg: string; label: string; Icon: React.ElementType }
> = {
    ACTIVE: {
        bg: '#6570E618',
        fg: '#6570E6',
        label: 'Active',
        Icon: Schedule,
    },
    COMPLETED: {
        bg: '#d1fae5',
        fg: '#047857',
        label: 'Completed',
        Icon: CheckCircleOutline,
    },
    STOPPED: {
        bg: '#f3f4f6',
        fg: '#6b7280',
        label: 'Stopped',
        Icon: RemoveCircleOutline,
    },
}

const CHANNEL_ICON: Record<DeliveryLogChannel, React.ElementType> = {
    email: EmailIcon,
    sms: SmsIcon,
    portal: PortalIcon,
}

const CHANNEL_LABEL: Record<DeliveryLogChannel, string> = {
    email: 'Email',
    sms: 'SMS',
    portal: 'Portal',
}

const LOG_STATUS_STYLE: Record<
    DeliveryLogStatus,
    { bg: string; fg: string; Icon: React.ElementType }
> = {
    sent: { bg: '#d1fae5', fg: '#047857', Icon: CheckCircleOutline },
    failed: { bg: '#ffe4e6', fg: '#be123c', Icon: ErrorOutline },
    skipped: { bg: '#f3f4f6', fg: '#6b7280', Icon: RemoveCircleOutline },
}

const NO_SEQUENCE_VALUE = -1

interface RenewalOfferPanelProps {
    offer: RenewalOffer
    sequences: RenewalSequence[]
    readOnly?: boolean
    onChangeSequence?: (offerId: number, sequence: number | null) => void
    isChangingSequence?: boolean
}

function formatOfferDate(value: string | null | undefined): string {
    if (!value) return '—'
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export const RenewalOfferPanel: React.FC<RenewalOfferPanelProps> = ({
    offer,
    sequences,
    readOnly = false,
    onChangeSequence,
    isChangingSequence,
}) => {
    const classes = useStyles()
    const { enrollment, isLoading: enrollmentLoading } = useOfferEnrollment(
        offer.id,
    )
    const { logs, isLoading: logsLoading } = useOfferDeliveryLogs(offer.id)
    const [changeMode, setChangeMode] = useState(false)
    const [newSequence, setNewSequence] = useState(NO_SEQUENCE_VALUE)

    useEffect(() => {
        setChangeMode(false)
        setNewSequence(enrollment?.sequence ?? NO_SEQUENCE_VALUE)
    }, [offer.id, enrollment])

    const isOffered = offer.status === 'offered'
    const canChangeSequence = !readOnly && isOffered && onChangeSequence != null
    const boardStage = offerBoardStage(offer)
    const leaseLabel =
        boardStage === 'future_resident'
            ? 'Signed'
            : boardStage === 'signing'
            ? signingSubstatusLabel(signingSubstatus(offer))
            : renewalStatusLabel(offer.status)

    const handleConfirmChange = () => {
        if (!onChangeSequence) return
        onChangeSequence(
            offer.id,
            newSequence === NO_SEQUENCE_VALUE ? null : newSequence,
        )
        setChangeMode(false)
    }

    return (
        <>
            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Renewal offer
                </Typography>
                <Box className={classes.grid}>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Status
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {leaseLabel}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Unit
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {offer.unit_name} · {offer.area_config_name}{' '}
                            {offer.area_label}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Proposed rent
                        </Typography>
                        <Typography
                            className={classes.fieldValue}
                            style={{
                                color: '#6570E6',
                                fontFamily: 'monospace',
                            }}
                        >
                            {formatCurrency(offer.proposed_rent)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Term
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {offer.proposed_term_months} month
                            {offer.proposed_term_months === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Start
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {formatOfferDate(offer.proposed_start_date)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            End
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {formatOfferDate(offer.proposed_end_date)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.fieldLabel}>
                            Concessions
                        </Typography>
                        {offer.concession_names?.length > 0 ? (
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                style={{ gap: 4 }}
                            >
                                {offer.concession_names.map((name, index) => (
                                    <Chip
                                        key={index}
                                        label={name}
                                        size="small"
                                        style={{
                                            backgroundColor: '#d1fae5',
                                            color: '#047857',
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                            height: 20,
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography className={classes.fieldValue}>
                                —
                            </Typography>
                        )}
                    </Box>
                    {offer.offered_date && (
                        <Box>
                            <Typography className={classes.fieldLabel}>
                                Offered
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {displayDateTime(offer.offered_date)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {offer.packet_send_error && (
                <Box className={classes.alert}>
                    <Typography
                        variant="subtitle2"
                        style={{ color: '#be123c', fontWeight: 700 }}
                    >
                        Lease packet was not sent
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{ color: '#9f1239', marginTop: 4 }}
                    >
                        {offer.packet_send_error}
                    </Typography>
                </Box>
            )}

            {offer.status === 'declined' && offer.decline_reason && (
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Decline reason
                    </Typography>
                    <Box className={classes.alert}>
                        <Typography
                            variant="body2"
                            style={{
                                whiteSpace: 'pre-wrap',
                                color: '#881337',
                                fontSize: '0.8125rem',
                            }}
                        >
                            {offer.decline_reason}
                        </Typography>
                        {offer.responded_at && (
                            <Typography
                                variant="caption"
                                style={{
                                    color: '#9f1239',
                                    display: 'block',
                                    marginTop: 8,
                                }}
                            >
                                Declined on{' '}
                                {displayDateTime(offer.responded_at)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {offer.status === 'transfer_requested' && (
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Transfer request
                    </Typography>
                    <Box className={classes.card}>
                        <Box className={classes.cardTop}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 8 }}
                            >
                                <TransferWithinAStation
                                    style={{ fontSize: 18, color: '#b45309' }}
                                />
                                <Typography
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.8125rem',
                                    }}
                                >
                                    Tenant requested a unit transfer
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={classes.cardBody}>
                            {offer.transfer_desired_unit_config_name && (
                                <Box>
                                    <Typography className={classes.fieldLabel}>
                                        Desired floor plan
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        {
                                            offer.transfer_desired_unit_config_name
                                        }
                                    </Typography>
                                </Box>
                            )}
                            {offer.transfer_roommates?.length > 0 && (
                                <Box>
                                    <Typography className={classes.fieldLabel}>
                                        <Box
                                            display="inline-flex"
                                            alignItems="center"
                                            style={{ gap: 4 }}
                                        >
                                            <PeopleIcon
                                                style={{ fontSize: 13 }}
                                            />
                                            Roommates (
                                            {offer.transfer_roommates.length})
                                        </Box>
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        mt={0.75}
                                        style={{ gap: 4 }}
                                    >
                                        {offer.transfer_roommates.map(
                                            (roommate) => (
                                                <Chip
                                                    key={roommate.user_id}
                                                    label={roommate.name}
                                                    size="small"
                                                />
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            )}
                            {offer.transfer_notes && (
                                <Box style={{ gridColumn: '1 / -1' }}>
                                    <Typography className={classes.fieldLabel}>
                                        Notes
                                    </Typography>
                                    <Typography
                                        className={classes.fieldValue}
                                        style={{
                                            fontWeight: 500,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {offer.transfer_notes}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Sequence enrollment
                </Typography>
                {enrollmentLoading ? (
                    <CircularProgress size={20} />
                ) : enrollment ? (
                    <Box className={classes.card}>
                        <Box className={classes.cardTop}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 8 }}
                            >
                                <Typography
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {enrollment.sequence_name}
                                </Typography>
                                {(() => {
                                    const status =
                                        ENROLLMENT_STATUS[enrollment.status]
                                    return (
                                        <Chip
                                            icon={
                                                <status.Icon
                                                    style={{
                                                        fontSize: 14,
                                                        color: status.fg,
                                                    }}
                                                />
                                            }
                                            label={status.label}
                                            size="small"
                                            style={{
                                                backgroundColor: status.bg,
                                                color: status.fg,
                                                fontWeight: 700,
                                                fontSize: '0.625rem',
                                                height: 22,
                                            }}
                                        />
                                    )
                                })()}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                                Step #{enrollment.current_step_order} ·{' '}
                                {enrollment.current_step_type}
                            </Typography>
                        </Box>
                        <Box className={classes.cardBody}>
                            <Box>
                                <Typography className={classes.fieldLabel}>
                                    Started
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {displayDateTime(enrollment.started_at)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography className={classes.fieldLabel}>
                                    Last sent
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {displayDateTime(
                                        enrollment.last_step_sent_at,
                                    )}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography className={classes.fieldLabel}>
                                    Next scheduled
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {displayDateTime(
                                        enrollment.next_step_scheduled_at,
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                        {canChangeSequence && (
                            <Box className={classes.changeRow}>
                                {changeMode ? (
                                    <SequencePicker
                                        sequences={sequences}
                                        value={newSequence}
                                        onChange={setNewSequence}
                                        onApply={handleConfirmChange}
                                        onCancel={() => setChangeMode(false)}
                                        isChanging={isChangingSequence}
                                    />
                                ) : (
                                    <Tooltip title="Switch sequence or stop messaging">
                                        <Button
                                            size="small"
                                            startIcon={
                                                <SwapHoriz fontSize="small" />
                                            }
                                            onClick={() => setChangeMode(true)}
                                            style={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Change sequence
                                        </Button>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Typography className={classes.empty}>
                            Not enrolled in a sequence.
                        </Typography>
                        {canChangeSequence && (
                            <Box mt={1}>
                                {changeMode ? (
                                    <SequencePicker
                                        sequences={sequences}
                                        value={newSequence}
                                        onChange={setNewSequence}
                                        onApply={handleConfirmChange}
                                        onCancel={() => setChangeMode(false)}
                                        isChanging={isChangingSequence}
                                    />
                                ) : (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            <SwapHoriz fontSize="small" />
                                        }
                                        onClick={() => setChangeMode(true)}
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Assign sequence
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Delivery log
                    {logs.length > 0 ? ` · ${logs.length}` : ''}
                </Typography>
                {logsLoading ? (
                    <CircularProgress size={20} />
                ) : logs.length === 0 ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <InboxOutlined fontSize="small" color="disabled" />
                        <Typography className={classes.empty}>
                            No messages delivered yet.
                        </Typography>
                    </Box>
                ) : (
                    <Box className={classes.card}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead className={classes.logsHead}>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Step</TableCell>
                                        <TableCell>Channel</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={classes.logsBody}>
                                    {logs.map((log) => {
                                        const statusStyle =
                                            LOG_STATUS_STYLE[log.status] ??
                                            LOG_STATUS_STYLE.sent
                                        const ChannelIcon =
                                            CHANNEL_ICON[log.channel] ??
                                            EmailIcon
                                        return (
                                            <TableRow key={log.id}>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {displayDateTime(
                                                            log.created_date,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        #{log.step_order}{' '}
                                                        {log.step_type}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <ChannelIcon
                                                            style={{
                                                                fontSize: 14,
                                                                marginRight: 4,
                                                                color:
                                                                    '#6b7280',
                                                            }}
                                                        />
                                                        <Typography variant="caption">
                                                            {CHANNEL_LABEL[
                                                                log.channel
                                                            ] ?? log.channel}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={
                                                            <statusStyle.Icon
                                                                style={{
                                                                    fontSize: 13,
                                                                    color:
                                                                        statusStyle.fg,
                                                                }}
                                                            />
                                                        }
                                                        label={log.status}
                                                        size="small"
                                                        style={{
                                                            backgroundColor:
                                                                statusStyle.bg,
                                                            color:
                                                                statusStyle.fg,
                                                            fontWeight: 700,
                                                            fontSize: '0.6rem',
                                                            height: 20,
                                                            textTransform:
                                                                'capitalize',
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Box>
        </>
    )
}

function SequencePicker({
    sequences,
    value,
    onChange,
    onApply,
    onCancel,
    isChanging,
}: {
    sequences: RenewalSequence[]
    value: number
    onChange: (value: number) => void
    onApply: () => void
    onCancel: () => void
    isChanging?: boolean
}) {
    return (
        <>
            <TextField
                select
                variant="outlined"
                size="small"
                value={value}
                onChange={(event) => onChange(Number(event.target.value))}
                style={{ minWidth: 180, flex: 1 }}
            >
                <MenuItem value={NO_SEQUENCE_VALUE}>
                    <em>Stop messaging</em>
                </MenuItem>
                {sequences.map((sequence) => (
                    <MenuItem key={sequence.id} value={sequence.id}>
                        {sequence.name}
                        {sequence.is_active ? ' (active)' : ''}
                    </MenuItem>
                ))}
            </TextField>
            <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={onApply}
                disabled={isChanging}
                style={{ textTransform: 'none', fontWeight: 600 }}
            >
                {isChanging ? <CircularProgress size={16} /> : 'Apply'}
            </Button>
            <Button
                size="small"
                onClick={onCancel}
                style={{ textTransform: 'none', fontWeight: 600 }}
            >
                Cancel
            </Button>
        </>
    )
}
